"use strict";
// Feature: import
//
// ImportBatch — source-agnostic audit record for one import run.
//
// Generalises the legacy `FreeAgentImport` concept. Every source's runs are
// audited uniformly — the `sourceSystem` discriminator identifies which
// adapter produced the batch. A legacy `FreeAgentImport` row is read as an
// `ImportBatch` whose `sourceSystem` is `freeagent` (Import Requirement 5.4).
//
// Design ref: `.kiro/specs/import/design.md` §"Data Models" §"ImportBatch".
// Requirements: import Req 5.1, 5.2, 5.3, 5.4, 9.1.
Object.defineProperty(exports, "__esModule", { value: true });
exports.fromLegacyRow = exports.zeroCounts = void 0;
/**
 * Zero-valued counts for a freshly-created batch. Callers that need to build
 * a `pending` batch use this so no numeric field is left `undefined`.
 */
function zeroCounts() {
    return {
        imported: 0,
        skipped: 0,
        preservedReclassified: 0,
        flaggedDiscrepancies: 0,
        ambiguous: 0,
    };
}
exports.zeroCounts = zeroCounts;
/**
 * Read a possibly-legacy `FreeAgentImport` row (as written by the deployed
 * `confirmHandler` before this model existed) as an `ImportBatch`. Missing
 * `sourceSystem` is normalised to `'freeagent'` (Requirement 5.4); missing
 * structured `counts` are reconstructed from legacy flat fields.
 *
 * This function does NOT mutate the legacy row — callers rewriting rows must
 * do so explicitly. The function's job is read-side compatibility only.
 */
function fromLegacyRow(row) {
    const counts = zeroCounts();
    if (typeof row.imported === "number")
        counts.imported = Math.max(0, row.imported);
    if (typeof row.skipped === "number")
        counts.skipped = Math.max(0, row.skipped);
    if (typeof row.preservedReclassified === "number") {
        counts.preservedReclassified = Math.max(0, row.preservedReclassified);
    }
    if (typeof row.flaggedDiscrepancies === "number") {
        counts.flaggedDiscrepancies = Math.max(0, row.flaggedDiscrepancies);
    }
    if (typeof row.ambiguous === "number") {
        counts.ambiguous = Math.max(0, row.ambiguous);
    }
    // A structured `counts` object on the row takes precedence over legacy
    // flat fields when both are present.
    if (row.counts && typeof row.counts === "object" && row.counts !== null) {
        const c = row.counts;
        if (typeof c.imported === "number")
            counts.imported = Math.max(0, c.imported);
        if (typeof c.skipped === "number")
            counts.skipped = Math.max(0, c.skipped);
        if (typeof c.preservedReclassified === "number") {
            counts.preservedReclassified = Math.max(0, c.preservedReclassified);
        }
        if (typeof c.flaggedDiscrepancies === "number") {
            counts.flaggedDiscrepancies = Math.max(0, c.flaggedDiscrepancies);
        }
        if (typeof c.ambiguous === "number") {
            counts.ambiguous = Math.max(0, c.ambiguous);
        }
    }
    const rawStatus = typeof row.status === "string" ? row.status : "complete";
    const status = rawStatus === "pending" ||
        rawStatus === "processing" ||
        rawStatus === "complete" ||
        rawStatus === "failed"
        ? rawStatus
        : "complete";
    const errors = Array.isArray(row.errors)
        ? row.errors
            .filter((e) => typeof e === "object" && e !== null)
            .map((e) => ({
            sourceRef: typeof e.sourceRef === "string" ? e.sourceRef : undefined,
            message: typeof e.message === "string" ? e.message : String(e.message ?? ""),
        }))
        : [];
    return {
        id: String(row.id ?? ""),
        ownerId: String(row.ownerId ?? ""),
        buildingId: String(row.buildingId ?? ""),
        sourceSystem: typeof row.sourceSystem === "string" && row.sourceSystem.length > 0
            ? row.sourceSystem
            : "freeagent",
        importDate: String(row.importDate ?? row.createdAt ?? ""),
        status,
        counts,
        errors,
        createdAt: String(row.createdAt ?? row.importDate ?? ""),
        updatedAt: typeof row.updatedAt === "string" ? row.updatedAt : undefined,
    };
}
exports.fromLegacyRow = fromLegacyRow;
